import i18n from 'i18next';
import { logger } from 'packages';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { toLocalizedUrl } from 'tradera-utils/url';
import { formatNumberWithSeparators } from 'tradera-utils/format';
import 'moment/locale/sv';
import 'moment/locale/da';
import 'moment/locale/de';

let i18nInitialized;

/**
 * Initialize function for i18next
 * Must be run before doing any translations
 * @param {Array} [translations]
 * @param {Object} [options]
 * @returns {Promise<Function>} - a promise to the i18next t function
 */
export const bootLanguages = (
    translations = window.touchwebTranslations,
    attributeTranslations = window.attributeTranslations,
    options = {}
) => {
    const translationResources = Object.entries(translations).map(
        ([key, value]) => ({
            key,
            value,
            namespace: 'touchWeb'
        })
    );
    const attributeResources = Object.entries(
        attributeTranslations
    ).map(([key, value]) => ({ key, value, namespace: 'attributes' }));

    const resources = translationResources
        .concat(attributeResources)
        .reduce((prev, { key, value, namespace }) => {
            const s = prev[key] || {};
            s[namespace] = value;
            return {
                ...prev,
                [key]: s
            };
        }, {});

    i18nInitialized = i18n
        .use(initReactI18next)
        .use(LanguageDetector)
        .init(
            {
                detection: {
                    order: ['htmlTag'],
                    htmlTag: document.html
                },
                defaultNS: ['touchWeb'],
                ns: ['attributes'],
                fallbackLng: 'sv',
                resources,
                debug: false,
                interpolation: {
                    escape: str => str.replace(/{{/g, '').replace(/}}/g, ''),
                    format: (value, format) => {
                        if (format === 'formatNumber') {
                            return formatNumberWithSeparators(value);
                        }

                        return value;
                    }
                },
                react: {
                    useSuspense: false // loading from file currently breaks if this is true
                },
                ...missingKeyLoggingConfig,
                ...options
            },
            err => {
                if (err) {
                    logger('error when loading translations', err);
                }
            }
        );

    return i18nInitialized;
};

const missingKeyLoggingConfig = {
    saveMissing: true,
    saveMissingTo: 'current',
    missingKeyHandler: (language, namespace, key) => {
        logger(
            `TranslationMissing (javascript) - Unable to find key ${key} (namespace ${namespace}) in language ${language}!`
        );
    },
    missingInterpolationHandler: (text, value) => {
        logger(
            `InterpolationFailure (javascript) - Unable to interpolate the text "${text}" fully. The following placeholder values were missing: ${JSON.stringify(
                value
            )}`
        );
        return undefined;
    }
};

/**
 * This function helps you make sure that i18next init has completed before continuing
 */
export const whenInitialized = () => {
    if (!i18nInitialized) {
        throw new Error(
            "Can't call boot-languages whenInitialized before init!"
        );
    }
    return i18nInitialized.then(t => ({
        t,
        toLocalizedUrl: url => toLocalizedUrl(url, i18n.language)
    }));
};

/**
 * Only required for Storybook, do not use.
 */
export const changeLanguage = language => i18n.changeLanguage(language);

export const getCurrentLanguage = () => i18n.language;
